import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { ScrollPage } from '@/components/ScrollPage';
import { useContracts, useDeleteContract, useRestoreContract } from '@/hooks/useContracts';
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog';
import { ContractStatusBadge } from '@/components/contracts/ContractStatusBadge';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useBusinessUnits } from '@/hooks/useBusinessUnits';
import { Button } from '@/components/ui/button';
import { Plus, Search, ChevronLeft, ChevronRight, Trash2, Undo2, Pencil, X, FileText } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { formatCalendarDate } from '@/lib/dateFormat';
import { contractTypeLabel } from '@/lib/contractType';
import { formatNumber } from '@/lib/numberFormat';
import type { VolumeDistribution } from '@/types';
import type { Contract as ApiContract } from '@/services/contractService';

const SKELETON_ROWS = 8;

function RowAction({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export default function Contracts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedBU, setSelectedBU] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showDeleted, setShowDeleted] = useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<{ type: 'delete' | 'restore'; contract: ApiContract } | null>(null);
  const pageSize = 20;

  const deleteContract = useDeleteContract();
  const restoreContract = useRestoreContract();

  const isAdmin = user?.role === 'Admin' || false;
  const isManager = user?.role === 'BU Manager' || isAdmin || false;
  const userBusinessUnitId = user?.businessUnitId;

  // Fetch business units for filter dropdown
  const { data: businessUnits } = useBusinessUnits();

  // Fetch contracts with pagination and filters
  const { data: contractsData, isLoading, error } = useContracts({
    pageNumber: currentPage,
    pageSize,
    search: searchQuery || undefined,
    businessUnitId: selectedBU === 'all' ? undefined : parseInt(selectedBU),
    includeDeleted: isAdmin && showDeleted,
  });

  const hasActiveFilters = searchQuery !== '' || selectedBU !== 'all';

  const handleFilterChange = (value: string) => {
    setSelectedBU(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleShowDeletedChange = (checked: boolean) => {
    setShowDeleted(checked);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedBU('all');
    setCurrentPage(1);
  };

  const confirmPendingAction = () => {
    if (!pendingAction) return;
    const mutation = pendingAction.type === 'delete' ? deleteContract : restoreContract;
    mutation.mutate(pendingAction.contract.id, { onSettled: () => setPendingAction(null) });
  };

  const canUpdateContract = (contract: ApiContract) => {
    if (isAdmin) return true;
    // Manager can update if contract has VD in their BU
    if (isManager && userBusinessUnitId && contract.volumeDistributions?.some(
      (vd: VolumeDistribution) => vd.businessUnitId === parseInt(userBusinessUnitId)
    )) {
      return true;
    }
    return false;
  };

  if (error) {
    return (
      <Layout>
        <div role="alert" className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-sm text-destructive">
            Error loading contracts. Please try again later.
          </p>
        </div>
      </Layout>
    );
  }

  const totalCount = contractsData?.totalCount;

  return (
    <Layout>
      <ScrollPage
        framed={!isLoading && !!contractsData && contractsData.items.length > 0}
        footer={
      contractsData && contractsData.items.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, contractsData.totalCount)} of {contractsData.totalCount}
          </p>
          {contractsData.totalPages > 1 && (
            <nav aria-label="Pagination" className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={!contractsData.hasPreviousPage}
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Previous
                  </Button>
                  {Array.from({ length: Math.min(contractsData.totalPages, 5) }, (_, i) => {
                    let pageNum: number;
                    if (contractsData.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= contractsData.totalPages - 2) {
                      pageNum = contractsData.totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? 'default' : 'ghost'}
                        size="sm"
                        aria-current={currentPage === pageNum ? 'page' : undefined}
                        onClick={() => setCurrentPage(pageNum)}
                        className="h-8 w-8 p-0"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(contractsData.totalPages, prev + 1))}
                    disabled={!contractsData.hasNextPage}
                  >
                    Next
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </nav>
          )}
        </div>
      ) : null
        }
        header={
          <>
        <PageHeader
          title="Contracts"
          description={
            totalCount === undefined
              ? 'Manage and track all contracts across business units'
              : `${formatNumber(totalCount, 0)} ${totalCount === 1 ? 'contract' : 'contracts'}${hasActiveFilters ? ' match your filters' : ' across all business units'}`
          }
          actions={
            <Button asChild>
              <Link to="/contracts/new">
                <Plus className="h-4 w-4" />
                New Contract
              </Link>
            </Button>
          }
        />

        {/* Toolbar: search leads, filters follow, the admin-only toggle closes the row */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:w-80">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              aria-label="Search contracts"
              placeholder="Search by name, product or supplier"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9"
            />
          </div>
          <Combobox
            className="w-full sm:w-60"
            value={selectedBU}
            onValueChange={(value) => handleFilterChange(value || 'all')}
            options={[
              { value: 'all', label: 'All Business Units' },
              ...(businessUnits?.map((bu) => ({ value: bu.id.toString(), label: bu.name })) ?? []),
            ]}
            placeholder="Business Unit"
            searchPlaceholder="Search business units..."
            emptyMessage="No business units found."
          />
          {hasActiveFilters && (
            <Button variant="ghost" onClick={clearFilters} className="text-muted-foreground">
              <X className="mr-1.5 h-4 w-4" />
              Clear filters
            </Button>
          )}
          {isAdmin && (
            <div className="flex h-10 items-center gap-2 sm:ml-auto">
              <Switch id="show-deleted-contracts" checked={showDeleted} onCheckedChange={handleShowDeletedChange} />
              <Label htmlFor="show-deleted-contracts" className="cursor-pointer text-sm font-normal">
                Show deleted
              </Label>
            </div>
          )}
        </div>

          </>
        }
      >
        {isLoading ? (
          <Card className="overflow-hidden" aria-busy="true" aria-label="Loading contracts">
            <div className="divide-y">
              {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                <div key={i} className="flex items-center gap-6 px-6 py-4">
                  <div className="w-56 space-y-2">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                  <Skeleton className="h-4 w-36" />
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="hidden h-4 w-40 md:block" />
                  <Skeleton className="ml-auto h-4 w-16" />
                </div>
              ))}
            </div>
          </Card>
        ) : !contractsData || contractsData.items.length === 0 ? (
          <div className="flex flex-col items-center rounded-lg border border-dashed border-border px-6 py-16 text-center">
            <FileText className="h-10 w-10 text-muted-foreground/60" aria-hidden="true" />
            <p className="mt-4 text-lg font-semibold">
              {hasActiveFilters ? 'No contracts match these filters' : 'No contracts yet'}
            </p>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              {hasActiveFilters
                ? 'Try a different search term or choose another business unit.'
                : 'Create the first contract to start tracking volumes and prices per business unit.'}
            </p>
            <div className="mt-6">
              {hasActiveFilters ? (
                <Button variant="outline" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : (
                <Button asChild>
                  <Link to="/contracts/new">
                    <Plus className="mr-2 h-4 w-4" />
                    New Contract
                  </Link>
                </Button>
              )}
            </div>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableHead className="min-w-[14rem] pl-6">Contract</TableHead>
                  <TableHead className="hidden md:table-cell">Supplier</TableHead>
                  <TableHead className="hidden xl:table-cell">Contract Date</TableHead>
                  <TableHead className="hidden lg:table-cell">Period</TableHead>
                  <TableHead className="text-right">Total Volume</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[104px] pr-6 text-right">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contractsData.items.map((contract) => (
                  <TableRow
                    key={contract.id}
                    className={`cursor-pointer ${contract.isDeleted ? 'text-muted-foreground' : ''}`}
                    onClick={() => navigate(`/contracts/${contract.id}`)}
                  >
                    <TableCell className="pl-6">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/contracts/${contract.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-semibold text-foreground underline-offset-4 hover:text-primary hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {contract.contractName}
                        </Link>
                        {contract.isDeleted && <Badge variant="destructive">Deleted</Badge>}
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {contract.productName} · {contractTypeLabel(contract.contractType)}
                      </p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{contract.supplierName}</TableCell>
                    <TableCell className="hidden whitespace-nowrap xl:table-cell">
                      {formatCalendarDate(contract.contractDate)}
                    </TableCell>
                    <TableCell className="hidden whitespace-nowrap lg:table-cell">
                      {formatCalendarDate(contract.startDate)}
                      <span className="text-muted-foreground"> – </span>
                      {formatCalendarDate(contract.endDate)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right font-medium tabular-nums">
                      {formatNumber(contract.totalVolume / 1000, 1)} <span className="font-normal text-muted-foreground">tons</span>
                    </TableCell>
                    <TableCell>
                      <ContractStatusBadge startDate={contract.startDate} endDate={contract.endDate} />
                    </TableCell>
                    <TableCell className="pr-6" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {!contract.isDeleted && canUpdateContract(contract) && (
                          <RowAction label="Edit">
                            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                              <Link to={`/contracts/${contract.id}/edit`} aria-label={`Edit ${contract.contractName}`}>
                                <Pencil className="h-4 w-4" />
                              </Link>
                            </Button>
                          </RowAction>
                        )}
                        {isAdmin && (contract.isDeleted ? (
                          <RowAction label="Restore">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              aria-label={`Restore ${contract.contractName}`}
                              onClick={() => setPendingAction({ type: 'restore', contract })}
                            >
                              <Undo2 className="h-4 w-4" />
                            </Button>
                          </RowAction>
                        ) : (
                          <RowAction label="Delete">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                              aria-label={`Delete ${contract.contractName}`}
                              onClick={() => setPendingAction({ type: 'delete', contract })}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </RowAction>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

          </>
        )}
      </ScrollPage>

      <ConfirmActionDialog
        open={!!pendingAction}
        destructive={pendingAction?.type === 'delete'}
        isPending={deleteContract.isPending || restoreContract.isPending}
        title={pendingAction?.type === 'delete' ? 'Delete contract?' : 'Restore contract?'}
        description={
          pendingAction?.type === 'delete'
            ? `"${pendingAction.contract.contractName}" will be hidden from the contract list. You can restore it later.`
            : `"${pendingAction?.contract.contractName}" will become visible and editable again.`
        }
        confirmLabel={pendingAction?.type === 'delete' ? 'Delete' : 'Restore'}
        onConfirm={confirmPendingAction}
        onCancel={() => setPendingAction(null)}
      />
    </Layout>
  );
}
